/**
 * Created by david.vanoni@coplex.com on 8/21/17.
 */
import { SIGN_IN_SUCCESS, SIGN_OUT_SUCCESS } from '../modules/auth.js';
import {
  scheduleSessionWarning,
  cancelSessionWarning,
} from '../lib/sessionGuard.js';
import { camelizeKeys, decamelizeKeys } from 'humps';
import fetch from 'isomorphic-unfetch';
import param from 'jquery-param';
import { isPlainObject, isUndefined, omitBy } from 'lodash';
import { navigate } from '../lib/router.js';
import { buildPath } from '../components/Link.jsx';
import { normalize } from 'normalizr';
import {
  getAuthHeaders,
  removeAuthHeaders,
  setRefreshTokenExpiry,
} from '../lib/session.js';
import { updateAutoHiddenNotificationState } from '../modules/notifications.js';
import { refreshAccessToken } from '../lib/tokenRefresh.js';

// Available HTTP request methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
};

// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API = Symbol('Call API');

const UNAUTHORIZED_REDIRECT_PATH = '/';

const handleError = async (
  response,
  json,
  disabledCamelizeKeys,
  store,
  skipAuthRedirect
) => {
  // 419 = Access token expired, try refresh
  if (response.status === 419) {
    const apiUrl = store.getState().config.apiUrl;
    const newAuthToken = await refreshAccessToken(apiUrl);

    if (newAuthToken) {
      const payload = disabledCamelizeKeys ? json : camelizeKeys(json);
      return Promise.reject({
        status: response.status,
        shouldRetry: true,
        ...payload,
      });
    }

    // Refresh failed, clear auth and redirect
    removeAuthHeaders();

    const currentPath = window.location.pathname;

    if (currentPath !== UNAUTHORIZED_REDIRECT_PATH) {
      const errorMessage =
        json.message || 'Session expired. Please sign in again.';
      navigate(
        `${UNAUTHORIZED_REDIRECT_PATH}?error=${encodeURIComponent(errorMessage)}`,
        { replace: true }
      );
    }
  }

  // 401/403 = Not authenticated, redirect to login
  if (response.status === 401 || response.status === 403) {
    const sessionExpired = store.getState().auth.sessionExpired;
    if (sessionExpired) {
      const payload = disabledCamelizeKeys ? json : camelizeKeys(json);
      return Promise.reject({ status: response.status, ...payload });
    }
    removeAuthHeaders();

    const currentPath = window.location.pathname;
    if (!skipAuthRedirect && currentPath !== UNAUTHORIZED_REDIRECT_PATH) {
      const errorMessage =
        json.message || 'Authentication required. Please sign in.';
      navigate(
        `${UNAUTHORIZED_REDIRECT_PATH}?error=${encodeURIComponent(errorMessage)}`,
        { replace: true }
      );
    }
  }

  // 404 = Resource not found, redirect to error page
  if (response.status === 404) {
    // Extract org slug from current path to preserve context
    const pathname = window.location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    const slug = segments[0] || '';

    // Redirect to 404 page with org slug preserved
    const notFoundPath = slug ? `/${slug}/_not-found` : '/_not-found';
    navigate(notFoundPath, { replace: true });
  }

  const payload = disabledCamelizeKeys ? json : camelizeKeys(json);
  return Promise.reject({ status: response.status, ...payload });
};

const handleResponse =
  (schema, disabledCamelizeKeys, store, skipAuthRedirect) =>
  async (response) => {
    if (response.status === 204) {
      // No Content
      return response.statusText;
    }
    if (response.status >= 500) {
      // Server Error
      return Promise.reject(response.statusText);
    }

    // Assume JSON response
    const json = await response.json();

    if (!response.ok) {
      return handleError(
        response,
        json,
        disabledCamelizeKeys,
        store,
        skipAuthRedirect
      );
    }

    const camelizedJson = !disabledCamelizeKeys ? camelizeKeys(json) : json;

    return schema ? normalize(camelizedJson, schema) : camelizedJson;
  };

const callApi = (
  url,
  method,
  body,
  schema,
  disabledDecamelizeKeys,
  disabledCamelizeKeys,
  customHeader = {},
  withCredentials = false,
  store,
  skipAuthRedirect
) => {
  const requestOptions = {
    method,
    body,
    headers: {
      Accept: 'application/json',
      ...getAuthHeaders(),
      ...customHeader,
    },
  };
  if (withCredentials) {
    requestOptions.credentials = 'include';
  }
  if (isPlainObject(body)) {
    requestOptions.body = JSON.stringify(
      !disabledDecamelizeKeys ? decamelizeKeys(body) : body
    );
    requestOptions.headers['Content-Type'] = 'application/json';
  }
  return fetch(url, requestOptions).then(
    handleResponse(schema, disabledCamelizeKeys, store, skipAuthRedirect)
  );
};

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default (store) => (next) => (action) => {
  const callAPI = action[CALL_API];
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  const {
    query,
    body,
    types,
    schema,
    disabledDecamelizeKeys,
    disabledCamelizeKeys,
    customHeader,
    withCredentials,
    skipAuthRedirect,
  } = callAPI;
  let { endpoint, method } = callAPI;
  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL.');
  }

  // If query params are specified, append them to endpoint
  if (typeof query !== 'undefined') {
    if (!isPlainObject(query)) {
      throw new Error('Expected query to be an object.');
    }
    endpoint += `?${param(decamelizeKeys(omitBy(query, isUndefined)))}`;
  }

  // Build full URL by prepending API server URL
  const baseUrl = store.getState().config.apiUrl;
  const apiRoot = baseUrl.endsWith('/')
    ? baseUrl + 'api/v1/'
    : baseUrl + '/api/v1/';
  const fullUrl =
    endpoint.indexOf(apiRoot) === -1 ? apiRoot + endpoint : endpoint;

  if (typeof method === 'undefined') {
    method = HTTP_METHODS.GET;
  } else if (!{}.hasOwnProperty.call(HTTP_METHODS, method)) {
    throw new Error('Invalid HTTP method.');
  }

  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of three action types.');
  }
  if (!types.every((type) => typeof type === 'string')) {
    throw new Error('Expected action types to be strings.');
  }

  const actionWith = (data) => {
    const finalAction = { ...action, ...data };
    delete finalAction[CALL_API];
    return finalAction;
  };

  const [requestType, successType, failureType] = types;
  next(actionWith({ type: requestType }));

  const makeRequest = () => {
    return callApi(
      fullUrl,
      method,
      body,
      schema,
      disabledDecamelizeKeys,
      disabledCamelizeKeys,
      customHeader,
      withCredentials,
      store,
      skipAuthRedirect
    );
  };

  const handleFailure = (error) => {
    try {
      const isServerError =
        error && typeof error === 'object' && error.status >= 500;
      const isNetworkError = !error || typeof error === 'string';
      if (isServerError || isNetworkError) {
        const message =
          error?.message ||
          error?.error ||
          'Something went wrong. Please try again later. If the problem persists, contact our support team for assistance.';
        store.dispatch(
          updateAutoHiddenNotificationState({
            isShown: true,
            type: 'danger',
            forPath: window.location.pathname,
            message,
          })
        );
      }

      // 423 = the project is still being duplicated and cannot be opened yet.
      // Mutations keep the error for their form; pages go back to the projects list.
      if (error?.status === 423 && method === HTTP_METHODS.GET) {
        const projectsPath = buildPath('projects/index');

        store.dispatch(
          updateAutoHiddenNotificationState({
            isShown: true,
            type: 'warning',
            forPath: projectsPath,
            message: error.message,
          })
        );

        if (window.location.pathname !== projectsPath) {
          navigate(projectsPath, { replace: true });
        }
      }
    } catch (e) {}

    return Promise.resolve(
      next(
        actionWith({
          type: failureType,
          error: error || 'Something bad happened',
        })
      )
    ).then((err) => Promise.reject(err.error ? err.error : err));
  };

  return makeRequest().then(
    (response) => {
      return Promise.resolve(
        next(
          actionWith({
            payload: response,
            type: successType,
          })
        )
      ).then((result) => {
        if (successType === SIGN_IN_SUCCESS && response.authToken) {
          setRefreshTokenExpiry(response.refreshTokenExpiresAt);
          scheduleSessionWarning(response.refreshTokenExpiresAt);
          // scheduleSessionWarning(new Date(Date.now() + 15000).toISOString()); // test
        }
        if (successType === SIGN_OUT_SUCCESS) {
          cancelSessionWarning();
        }
        return result.payload ? result.payload : result;
      });
    },
    (error) => {
      if (error?.shouldRetry) {
        // Token refreshed, retry the request
        return makeRequest().then(
          (response) => {
            return Promise.resolve(
              next(
                actionWith({
                  payload: response,
                  type: successType,
                })
              )
            ).then((result) => {
              if (successType === SIGN_IN_SUCCESS && response.authToken) {
                setRefreshTokenExpiry(response.refreshTokenExpiresAt);
                scheduleSessionWarning(response.refreshTokenExpiresAt);
                // scheduleSessionWarning(
                //   new Date(Date.now() + 15000).toISOString()
                // ); // test
              }
              if (successType === SIGN_OUT_SUCCESS) {
                cancelSessionWarning();
              }
              return result.payload ? result.payload : result;
            });
          },
          (retryError) => {
            return handleFailure(retryError);
          }
        );
      }

      return handleFailure(error);
    }
  );
};
